// src/components/aiinsight/SessionSidebar.jsx
// Left panel: session history + new chat — styled like Antigravity's history sidebar

import React, { useMemo, useRef, useState } from 'react';
import {
    Box, IconButton, InputAdornment, List, ListItem,
    ListItemButton, ListItemText, Skeleton, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MenuIcon from '@mui/icons-material/Menu';

import dayjs from 'dayjs';
import { getActiveTimezone } from '../../utils/dateTime';

// ── Group sessions by recency (Timezone Aware) ────────────────────────────────
const groupSessions = (sessions) => {
    const activeTZ = getActiveTimezone();
    const now = dayjs().tz(activeTZ);
    const startOfToday = now.startOf('day');
    const startOfYesterday = startOfToday.subtract(1, 'day');
    const startOfLastWeek = startOfToday.subtract(7, 'day');

    const groups = { Today: [], Yesterday: [], 'Last 7 days': [], Older: [] };
    
    (sessions || []).forEach((s) => {
        if (!s.updated_at) return;
        
        // Parse as UTC then shift to active timezone
        const d = dayjs.utc(s.updated_at).tz(activeTZ);
        
        if (d.isAfter(startOfToday) || d.isSame(startOfToday)) {
            groups.Today.push(s);
        } else if (d.isAfter(startOfYesterday) || d.isSame(startOfYesterday)) {
            groups.Yesterday.push(s);
        } else if (d.isAfter(startOfLastWeek) || d.isSame(startOfLastWeek)) {
            groups['Last 7 days'].push(s);
        } else {
            groups.Older.push(s);
        }
    });
    return groups;
};

// ── Main component ─────────────────────────────────────────────────────────────
const SessionSidebar = ({
    sessions = [],
    activeSessionId,
    onSelectSession,
    onNewSession,
    onDeleteSession,
    loading = false,
    isOpen = true,
    onToggle,
    onRenameSession,
}) => {
    const [search, setSearch] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editingTitle, setEditingTitle] = useState('');
    const editInputRef = useRef(null);

    const groups = useMemo(() => groupSessions(sessions), [sessions]);

    // Filter sessions if search active
    const filteredGroups = useMemo(() => {
        if (!search.trim()) return groups;
        const q = search.toLowerCase();
        const result = {};
        Object.entries(groups).forEach(([label, group]) => {
            result[label] = group.filter(s => (s.title || '').toLowerCase().includes(q));
        });
        return result;
    }, [groups, search]);

    const startEdit = (session, e) => {
        e.stopPropagation();
        setEditingId(session.id);
        setEditingTitle(session.title || '');
        setTimeout(() => editInputRef.current?.select(), 50);
    };

    const commitEdit = (sessionId) => {
        if (editingTitle.trim()) onRenameSession?.(sessionId, editingTitle.trim());
        setEditingId(null);
    };

    // ── COLLAPSED: slim 48px strip ─────────────────────────────────────────────
    if (!isOpen) {
        return (
            <Box sx={{
                width: 48,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                bgcolor: 'background.paper',
                borderRight: '1px solid',
                borderColor: 'divider',
                flexShrink: 0,
                py: 1,
                gap: 0.5,
            }}>
                <Tooltip title="New chat" placement="right">
                    <IconButton size="small" onClick={onNewSession} sx={{
                        color: 'text.secondary',
                        '&:hover': { color: 'var(--primary-color)', bgcolor: 'rgba(var(--primary-rgb), 0.08)' },
                    }}>
                        <AddIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="History" placement="right">
                    <IconButton size="small" sx={{ color: 'text.disabled' }}>
                        <HistoryIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip title="Expand sidebar" placement="right">
                    <IconButton size="small" onClick={onToggle} sx={{
                        color: 'text.secondary',
                        '&:hover': { color: 'var(--primary-color)', bgcolor: 'rgba(var(--primary-rgb), 0.08)' },
                        mb: 1
                    }}>
                        <MenuIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Tooltip>
            </Box>
        );
    }

    // ── EXPANDED ───────────────────────────────────────────────────────────────
    return (
        <Box sx={{
            width: 260,
            minWidth: 220,
            maxWidth: 300,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
            overflow: 'hidden',
        }}>
            {/* ── Header ── */}
            <Box sx={{
                px: 1.5,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minHeight: 48,
                flexShrink: 0,
            }}>
                {/* Brand mark */}
                <AutoAwesomeIcon sx={{ color: '#818cf8', fontSize: 17, flexShrink: 0 }} />
                <Typography variant="subtitle2" sx={{
                    fontWeight: 700, fontSize: 13.5, letterSpacing: 0.1,
                    color: 'text.primary', flex: 1,
                }}>
                    Agentic Insight
                </Typography>

                {/* Collapse */}
                <Tooltip title="Collapse" placement="bottom">
                    <IconButton size="small" onClick={onToggle} sx={{
                        width: 30, height: 30,
                        color: 'text.secondary',
                        '&:hover': { color: 'var(--primary-color)', bgcolor: 'rgba(var(--primary-rgb), 0.1)' },
                    }}>
                        <MenuOpenIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* ── Search ── */}
            <Box sx={{ px: 1.5, py: 1, flexShrink: 0 }}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Search chats…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                            </InputAdornment>
                        ),
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearch('')} sx={{ p: 0.25 }}>
                                    <ClearIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2.5,
                            fontSize: 12.5,
                            bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#f8fafc',
                            '& fieldset': { borderColor: 'transparent' },
                            '&:hover fieldset': { borderColor: 'divider' },
                            '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1.5 },
                        },
                    }}
                />
            </Box>

            {/* ── Session list ── */}
            <Box sx={{
                flex: 1,
                overflowY: 'auto',
                pb: 2,
                '&::-webkit-scrollbar': { width: 4 },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.1)', borderRadius: 2 },
            }}>
                {loading ? (
                    <Box sx={{ px: 1.5, pt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {[1, 2, 3, 4].map((i) => (
                            <Skeleton key={i} height={36} sx={{ borderRadius: 2, bgcolor: 'action.hover' }} />
                        ))}
                    </Box>
                ) : (sessions || []).length === 0 ? (
                    <Box sx={{ pt: 6, pb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <ChatBubbleOutlineIcon sx={{ color: 'text.disabled', fontSize: 32 }} />
                        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', textAlign: 'center', fontSize: 12.5 }}>
                            No chat history yet
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', textAlign: 'center', fontSize: 11 }}>
                            Click <b>+</b> to start a new chat
                        </Typography>
                    </Box>
                ) : (
                    Object.entries(filteredGroups).map(([label, group]) =>
                        group.length > 0 ? (
                            <Box key={label}>
                                {/* Date group label */}
                                <Typography variant="caption" sx={{
                                    display: 'block',
                                    px: 2, pt: 1.5, pb: 0.5,
                                    color: 'text.disabled',
                                    fontWeight: 700,
                                    fontSize: 10,
                                    letterSpacing: 0.8,
                                    textTransform: 'uppercase',
                                }}>
                                    {label}
                                </Typography>

                                <List dense disablePadding sx={{ px: 1 }}>
                                    {group.map((session) => (
                                        <ListItem
                                            key={session.id}
                                            disablePadding
                                            sx={{
                                                mb: 0.25,
                                                '&:hover .session-actions': { opacity: 1 },
                                            }}
                                        >
                                            <ListItemButton
                                                selected={activeSessionId === session.id}
                                                onClick={() => !editingId && onSelectSession(session)}
                                                onDoubleClick={(e) => startEdit(session, e)}
                                                sx={{
                                                    borderRadius: 2,
                                                    py: 0.7,
                                                    pl: 1.5,
                                                    pr: 0.5,
                                                    gap: 0,
                                                    '&.Mui-selected': {
                                                        bgcolor: (t) =>
                                                            t.palette.mode === 'dark'
                                                                ? 'rgba(var(--primary-rgb), 0.18)'
                                                                : 'rgba(var(--primary-rgb), 0.09)',
                                                        '& .MuiListItemText-primary': {
                                                            color: 'var(--primary-color)',
                                                            fontWeight: 600,
                                                        },
                                                    },
                                                    '&.Mui-selected:hover': {
                                                        bgcolor: (t) =>
                                                            t.palette.mode === 'dark'
                                                                ? 'rgba(var(--primary-rgb), 0.24)'
                                                                : 'rgba(var(--primary-rgb), 0.14)',
                                                    },
                                                    '&:hover': { bgcolor: 'action.hover' },
                                                }}
                                            >
                                                {editingId === session.id ? (
                                                    <input
                                                        ref={editInputRef}
                                                        value={editingTitle}
                                                        onChange={(e) => setEditingTitle(e.target.value)}
                                                        onBlur={() => commitEdit(session.id)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') commitEdit(session.id);
                                                            if (e.key === 'Escape') setEditingId(null);
                                                            e.stopPropagation();
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{
                                                            width: '100%', fontSize: 13,
                                                            fontFamily: 'inherit', border: 'none',
                                                            outline: '1.5px solid var(--primary-color)',
                                                            borderRadius: 4, padding: '2px 6px',
                                                            background: 'transparent', color: 'inherit',
                                                        }}
                                                    />
                                                ) : (
                                                    <>
                                                        <ListItemText
                                                            primary={session.title || 'Unnamed Chat'}
                                                            primaryTypographyProps={{
                                                                noWrap: true,
                                                                sx: {
                                                                    fontSize: 13,
                                                                    color: activeSessionId === session.id
                                                                        ? 'var(--primary-color)'
                                                                        : 'text.primary',
                                                                    fontWeight: activeSessionId === session.id ? 600 : 400,
                                                                },
                                                            }}
                                                        />
                                                        {/* Hover actions */}
                                                        <Box
                                                            className="session-actions"
                                                            sx={{
                                                                display: 'flex',
                                                                opacity: 0,
                                                                transition: 'opacity 0.12s',
                                                                flexShrink: 0,
                                                                ml: 0.5,
                                                            }}
                                                        >
                                                            <Tooltip title="Rename" placement="top">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => startEdit(session, e)}
                                                                    sx={{
                                                                        p: 0.4,
                                                                        color: 'text.secondary',
                                                                        '&:hover': { color: 'var(--primary-color)', bgcolor: 'rgba(var(--primary-rgb), 0.1)' },
                                                                    }}
                                                                >
                                                                    <EditOutlinedIcon sx={{ fontSize: 13 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Delete" placement="top">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => { e.stopPropagation(); onDeleteSession?.(session.id); }}
                                                                    sx={{
                                                                        p: 0.4,
                                                                        color: 'text.secondary',
                                                                        '&:hover': { color: 'error.main', bgcolor: 'error.lighter' },
                                                                    }}
                                                                >
                                                                    <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    </>
                                                )}
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        ) : null
                    )
                )}

                {/* No search results */}
                {search.trim() && !loading && Object.values(filteredGroups).every(g => g.length === 0) && (
                    <Box sx={{ pt: 4, textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 12 }}>
                            No chats matching "{search}"
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default SessionSidebar;
